import { z } from 'zod'
import { parseRequestError } from '@/common/utilities.ts'
import axiosInstance from '@/libs/axios_config.ts'

const ApplicationsResponse = z.object({
  id: z.number(),
})
export type PublicProgramsResponse = z.infer<typeof ApplicationsResponse>

const GetApplicationResponseSchema = z.object({
  id: z.number(),
  address: z.string().nullable(),
  family_account_id: z.number(),
  application_id: z.number().nullable(),
  email: z.string().nullable(),
  language_preference_id: z.number().nullable(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  user_id: z.number().nullable(),
  status: z.number().nullable(),
  city: z.string().nullable(),
  global_state_id: z.number().nullable(),
  household_inhabitants: z.number().nullable(),
  household_income_greater: z.string().nullable(),
  household_income_type: z.string().nullable(),
  non_taxable_income: z.string().nullable(),
  household_filed_federal_income: z.string().nullable(),
  students: z
    .object({
      SSN: z.string().nullable(),
      date_of_birth: z.string().nullable(),
      grade: z.string().nullable(),
      id: z.number(),
      name: z.string().nullable(),
      public_scholarships: z.string().nullable(),
      relationship: z.string().nullable(),
      scholarships: z.string().nullable(),
      status: z.number().nullable(),
    })
    .nullable(),
  other_students: z
    .array(
      z.object({
        date_of_birth: z.string().nullable(),
        grade: z.string().nullable(),
        id: z.number(),
        name: z.string().nullable(),
        public_scholarships: z.string().nullable(),
        relationship: z.string().nullable(),
        scholarships: z.string().nullable(),
        status: z.number().nullable(),
      })
    )
    .nullable(),
  family_attestations: z.array(
    z.object({
      id: z.number(),
      order: z.number(),
      status: z.number(),
      program_attestation_id: z.number(),
    })
  ),
})

export type ApplicationResponse = z.infer<typeof GetApplicationResponseSchema>

export const GETScholarshipApplication = async (
  scholarship_applications_id: number | null
): Promise<ApplicationResponse> => {
  try {
    const response = await axiosInstance.get(
      `/family_application/${scholarship_applications_id}`
    )
    return GetApplicationResponseSchema.parse({ ...response.data })
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

const CompetingProgramSchema = z.object({
  id: z.number(),
  status: z.number(),
  name: z.string(),
})

export type CompetingProgramResponse = z.infer<typeof CompetingProgramSchema>

export const GETCompetingPrograms = async (
  programId: number
): Promise<CompetingProgramResponse[]> => {
  try {
    const response = await axiosInstance.get(
      '/scholar/family_application/global_programs',
      {
        params: {
          program_id: programId,
        },
      }
    )

    return z.array(CompetingProgramSchema).parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}

const AcctAttestationTextSchema = z.object({
  id: z.number(),
  order: z.number(),
  attestation_text: z.array(z.string()),
  code: z.string().nullable(),
})

export type AcctAttestationTextResponse = z.infer<
  typeof AcctAttestationTextSchema
>

export async function GETAttestationText(
  programId: number
): Promise<AcctAttestationTextResponse[]> {
  try {
    const response = await axiosInstance.get(
      '/family_application/attestations',
      { params: { program_id: programId } }
    )
    return z.array(AcctAttestationTextSchema).parse(response.data)
  } catch (error) {
    throw new Error(parseRequestError(error).message)
  }
}
